import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import {
  CreditCardIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhoneIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
} from 'lucide-react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface Plan {
  id: number;
  name: string;
  description?: string;
  concurrent_calls: number;
  monthly_calls: number | null;
  price: number | string;
  is_active: boolean;
  created_at: string;
  services_count?: number;
}

const AdminPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const [planData, setPlanData] = useState({
    name: '',
    description: '',
    concurrent_calls: 1,
    monthly_calls: null as number | null,
    price: 0 as number,
    is_active: true,
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/plans');
      const plansData = response.data?.data?.plans || [];
      setPlans(plansData);
    } catch (err: any) {
      setError('Erro ao carregar planos');
      console.error('Plans error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/plans', planData);
      setShowCreateModal(false);
      resetForm();
      fetchPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar plano');
    }
  };

  const handleEditPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlan) return;

    try {
      await api.put(`/admin/plans/${selectedPlan.id}`, planData);
      setShowEditModal(false);
      setSelectedPlan(null);
      resetForm();
      fetchPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar plano');
    }
  };

  const handleDeletePlan = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este plano?')) {
      return;
    }

    try {
      await api.delete(`/admin/plans/${id}`);
      fetchPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir plano');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await api.post(`/admin/plans/${id}/toggle-status`);
      fetchPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao alterar status do plano');
    }
  };

  const resetForm = () => {
    setPlanData({
      name: '',
      description: '',
      concurrent_calls: 1,
      monthly_calls: null,
      price: 0,
      is_active: true,
    });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (plan: Plan) => {
    setSelectedPlan(plan);
    setPlanData({
      name: plan.name,
      description: plan.description || '',
      concurrent_calls: plan.concurrent_calls,
      monthly_calls: plan.monthly_calls,
      price: typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price,
      is_active: plan.is_active,
    });
    setShowEditModal(true);
  };

  const getStatusIcon = (is_active: boolean) => {
    return is_active 
      ? <CheckCircleIcon className="h-5 w-5 text-green-500" />
      : <XCircleIcon className="h-5 w-5 text-red-500" />;
  };

  const getStatusColor = (is_active: boolean) => {
    return is_active
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 'R$ 0,00';
    return `R$ ${numPrice.toFixed(2).replace('.', ',')}`;
  };

  const formatMonthlyCallsText = (monthly_calls: number | null) => {
    return monthly_calls === null ? 'Ilimitadas' : `${monthly_calls}/mês`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Planos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure e gerencie os planos de serviços VoIP
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Novo Plano
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Plans List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {plans.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Nenhum plano encontrado
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Comece adicionando seu primeiro plano de serviços.
            </p>
            <div className="mt-6">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Adicionar Plano
              </button>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {plans.map((plan) => (
              <li key={plan.id}>
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <CreditCardIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">
                            {plan.name}
                          </p>
                          <div className="ml-2 flex items-center">
                            {getStatusIcon(plan.is_active)}
                          </div>
                        </div>
                        <div className="mt-1 flex items-center space-x-4">
                          <p className="text-sm text-gray-500">
                            {formatPrice(plan.price)}/mês
                          </p>
                          <div className="flex items-center text-sm text-gray-500">
                            <PhoneIcon className="h-4 w-4 mr-1" />
                            {plan.concurrent_calls} simultâneas
                          </div>
                          <p className="text-sm text-gray-500">
                            {formatMonthlyCallsText(plan.monthly_calls)} ligações
                          </p>
                          <div className="flex items-center text-sm text-gray-500">
                            <span>{plan.services_count || 0} serviços</span>
                          </div>
                        </div>
                        {plan.description && (
                          <p className="mt-1 text-sm text-gray-500 max-w-md truncate">
                            {plan.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                          plan.is_active
                        )}`}
                      >
                        {plan.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleStatus(plan.id)}
                          className="text-blue-400 hover:text-blue-600"
                          title={plan.is_active ? 'Desativar' : 'Ativar'}
                        >
                          {plan.is_active ? (
                            <ToggleRightIcon className="h-4 w-4" />
                          ) : (
                            <ToggleLeftIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => openEditModal(plan)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Editar"
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePlan(plan.id)}
                          className="text-red-400 hover:text-red-600"
                          title="Excluir"
                          disabled={(plan.services_count || 0) > 0}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-500">
                        {new Date(plan.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create Plan Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    Criar Novo Plano
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Configure um novo plano de serviços VoIP
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleCreatePlan} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome do Plano
                  </label>
                  <input
                    type="text"
                    required
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={planData.name}
                    onChange={(e) =>
                      setPlanData({ ...planData, name: e.target.value })
                    }
                    placeholder="Ex: Plano Básico"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    rows={3}
                    value={planData.description}
                    onChange={(e) =>
                      setPlanData({ ...planData, description: e.target.value })
                    }
                    placeholder="Descrição opcional do plano"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Chamadas Simultâneas
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="999"
                      className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                      value={planData.concurrent_calls}
                      onChange={(e) =>
                        setPlanData({ ...planData, concurrent_calls: parseInt(e.target.value) })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preço Mensal (R$)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                      value={planData.price}
                      onChange={(e) =>
                        setPlanData({ ...planData, price: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ligações Mensais
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="monthly_calls_type"
                        checked={planData.monthly_calls === null}
                        onChange={() => setPlanData({ ...planData, monthly_calls: null })}
                        className="mr-2"
                      />
                      <span className="text-sm">Ilimitadas</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="monthly_calls_type"
                        checked={planData.monthly_calls !== null}
                        onChange={() => setPlanData({ ...planData, monthly_calls: 1000 })}
                        className="mr-2"
                      />
                      <span className="text-sm">Limitadas:</span>
                      {planData.monthly_calls !== null && (
                        <input
                          type="number"
                          min="1"
                          className="ml-2 w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                          value={planData.monthly_calls || 1000}
                          onChange={(e) =>
                            setPlanData({ ...planData, monthly_calls: parseInt(e.target.value) })
                          }
                        />
                      )}
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={planData.is_active ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setPlanData({
                        ...planData,
                        is_active: e.target.value === 'active',
                      })
                    }
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all transform hover:scale-105"
                  >
                    Criar Plano
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {showEditModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">
                    Editar Plano
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Atualize as configurações do plano
                  </p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              <form onSubmit={handleEditPlan} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nome do Plano
                  </label>
                  <input
                    type="text"
                    required
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={planData.name}
                    onChange={(e) =>
                      setPlanData({ ...planData, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Descrição
                  </label>
                  <textarea
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    rows={3}
                    value={planData.description}
                    onChange={(e) =>
                      setPlanData({ ...planData, description: e.target.value })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Chamadas Simultâneas
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="999"
                      className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                      value={planData.concurrent_calls}
                      onChange={(e) =>
                        setPlanData({ ...planData, concurrent_calls: parseInt(e.target.value) })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Preço Mensal (R$)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                      value={planData.price}
                      onChange={(e) =>
                        setPlanData({ ...planData, price: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ligações Mensais
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="monthly_calls_type"
                        checked={planData.monthly_calls === null}
                        onChange={() => setPlanData({ ...planData, monthly_calls: null })}
                        className="mr-2"
                      />
                      <span className="text-sm">Ilimitadas</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="monthly_calls_type"
                        checked={planData.monthly_calls !== null}
                        onChange={() => setPlanData({ ...planData, monthly_calls: planData.monthly_calls || 1000 })}
                        className="mr-2"
                      />
                      <span className="text-sm">Limitadas:</span>
                      {planData.monthly_calls !== null && (
                        <input
                          type="number"
                          min="1"
                          className="ml-2 w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                          value={planData.monthly_calls || 1000}
                          onChange={(e) =>
                            setPlanData({ ...planData, monthly_calls: parseInt(e.target.value) })
                          }
                        />
                      )}
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    className="block w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all bg-gray-50 focus:bg-white"
                    value={planData.is_active ? 'active' : 'inactive'}
                    onChange={(e) =>
                      setPlanData({
                        ...planData,
                        is_active: e.target.value === 'active',
                      })
                    }
                  >
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 border border-transparent rounded-xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-all transform hover:scale-105"
                  >
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPlansPage;